import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Loader2,
  Briefcase,
  MapPin,
  DollarSign,
  Clock,
  Users,
  Search,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { portalGet } from '@/lib/portal-api';

interface PortalJob {
  id: string;
  title: string;
  location: string | null;
  remoteType: string | null;
  jobType: string | null;
  salaryMin: number | null;
  salaryMax: number | null;
  salaryCurrency: string;
  status: string;
  submissionCount: number;
  postedAt: string | null;
  deadline: string | null;
}

async function fetchPortalJobs(search?: string): Promise<PortalJob[]> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  return portalGet<PortalJob[]>(`/jobs?${params.toString()}`);
}

function formatJobType(jobType: string | null): string {
  if (!jobType) return '';
  const labels: Record<string, string> = {
    full_time: 'Full Time',
    part_time: 'Part Time',
    contract: 'Contract',
    internship: 'Internship',
    temporary: 'Temporary',
  };
  return labels[jobType] || jobType;
}

function formatSalary(min: number | null, max: number | null, currency: string): string | null {
  if (!min && !max) return null;
  const fmt = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 0,
  });
  if (min && max) return `${fmt.format(min)} - ${fmt.format(max)}`;
  if (min) return `From ${fmt.format(min)}`;
  return `Up to ${fmt.format(max!)}`;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function PortalJobsPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const handleSearchChange = (value: string) => {
    setSearch(value);
    // Simple debounce
    setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  const { data: jobs, isLoading } = useQuery<PortalJob[]>({
    queryKey: ['portal', 'jobs', debouncedSearch],
    queryFn: () => fetchPortalJobs(debouncedSearch || undefined),
    refetchInterval: 60000,
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Jobs</h1>
        <p className="text-text-secondary mt-1">
          Active job openings for your company
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
        <Input
          placeholder="Search jobs..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Jobs Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-text-tertiary" />
        </div>
      ) : !jobs || jobs.length === 0 ? (
        <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen py-16 text-center">
          <Briefcase className="h-12 w-12 text-text-tertiary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary">No active jobs</h3>
          <p className="text-sm text-text-tertiary mt-2">
            {debouncedSearch
              ? 'No jobs match your search. Try a different term.'
              : 'There are currently no active job openings. Check back later.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {jobs.map((job) => {
            const salary = formatSalary(job.salaryMin, job.salaryMax, job.salaryCurrency);
            const isExpired = job.deadline ? new Date(job.deadline) < new Date() : false;

            return (
              <Link
                key={job.id}
                to={`/portal/submissions?jobId=${job.id}`}
                className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-5 hover:border-emerald-500/30 transition-all group"
              >
                {/* Header */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-text-primary group-hover:text-emerald-400 transition-colors truncate">
                      {job.title}
                    </h3>
                    {job.jobType && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white/5 text-text-secondary">
                        {formatJobType(job.jobType)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-2">
                  {job.location && (
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <MapPin className="h-3.5 w-3.5 text-text-tertiary flex-shrink-0" />
                      <span className="truncate">
                        {job.location}
                        {job.remoteType && (
                          <span className="text-text-tertiary ml-1">({job.remoteType})</span>
                        )}
                      </span>
                    </div>
                  )}

                  {salary && (
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <DollarSign className="h-3.5 w-3.5 text-text-tertiary flex-shrink-0" />
                      <span>{salary}</span>
                    </div>
                  )}

                  {job.deadline && (
                    <div
                      className={`flex items-center gap-2 text-sm ${isExpired ? 'text-red-400' : 'text-text-secondary'}`}
                    >
                      <Clock className="h-3.5 w-3.5 text-text-tertiary flex-shrink-0" />
                      <span>
                        {isExpired ? 'Expired' : 'Deadline'}: {formatDate(job.deadline)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-sm text-text-tertiary">
                    <Users className="h-3.5 w-3.5" />
                    <span>
                      {job.submissionCount} submission{job.submissionCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {job.postedAt && (
                    <span className="text-xs text-text-tertiary">
                      Posted {formatDate(job.postedAt)}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
